import HBMessage from "@/components/HBMessage";
import HBPopover from "@/components/HBPopover";
import { getVipTypeById } from "@/constants/vip";
import { cancelBizOrder } from "@/services";
import useRequest from "@/utils/useReq";
import { useCountDown } from "ahooks";
import { Skeleton } from "antd";
import classNames from "classnames";
import { round, uniqBy } from "lodash";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import HBIcon from "../../HBIcon";
import Pay from "../../Pay";
import { DetailPopoverContent } from "../shared/bottom";
import { VipDialogContext } from "../shared/configContext";
import CouponList, { getMatchedCoupons } from "./couponList";
import styles from "./index.module.less";

function getPayPrice(details, couponsPrice = 0) {
  const reduce = details.reduce(
    (pre, cur) => pre + (cur.activity?.price || cur.price) * (cur.number || 1),
    0
  );

  const price = round(reduce - couponsPrice, 2);
  return price >= 0 ? price : 0;
}

/**
 * @typedef {(order: Object)=>void} fn
 * @param {{
 *  details?: Object[],
 *  order?: Object,
 *  scene: "normal"|"upgrade"|"addition",
 *  currentVipType: "company"|"personal",
 *  banner?: string,
 *  protocols?: {name: string, href: string}[],
 *  onOrderChange: fn,
 *  onOrderDone: fn,
 *  onOrderFaild: fn,
 *  onOrderPaid: fn,
 *  onRefresh: ()=>void,
 *  onDisableChange: ()=>void,
 *  onTransfer: ()=>void,
 * } & import("react").HTMLAttributes} props
 * @returns
 */
export default function PayBottom({
  theme,
  details = [],
  scene = "normal",
  isVip = false,
  currentVipType = "company",
  hasSubscribeTip,
  protocols,
  onOrderChange,
  onOrderDone,
  onOrderFaild,
  onOrderPaid,
  onDisableChange,
  onTransfer,
  ...rest
} = {}) {
  const [isCouponArrowUp, setIsCouponArrowUp] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState([]);

  const discount = useMemo(() => {
    return details && details.find((el) => el.type == "discount");
  }, [details]);

  const vipType = useMemo(() => {
    if (details && details.length) {
      for (let i = 0; i < details.length; i++) {
        const d = details[i];
        const type = getVipTypeById(d.good?.vip_id);
        if (type) {
          return type;
        }
      }
    }

    return currentVipType;
  }, [currentVipType, details]);

  const [disabled, setDisabled] = useState(false);
  const [targetDate, setTargetDate] = useState();
  const orderRef = useRef();
  const [countdown] = useCountDown({
    targetDate,
  });

  const onOrderChangeWrap = useCallback(
    (order) => {
      if (order.status === 0) {
        setDisabled(true);
        setTargetDate(Date.now() + 5000);
        onDisableChange && onDisableChange(true);
      }
      orderRef.current = order;
      onOrderChange && onOrderChange();
    },
    [onDisableChange, onOrderChange]
  );

  const onCancelOrder = useCallback(async () => {
    if (countdown > 0) {
      return;
    }

    if (orderRef.current && countdown === 0 && orderRef.current.status === 0) {
      await cancelBizOrder({ order_no: orderRef.current.order_no });
      setDisabled(false);
      onDisableChange && onDisableChange(false);
      payRef.current.refresh();
    } else {
      HBMessage.error("订单已支付");
    }
  }, [countdown, onDisableChange]);

  const payRef = useRef();

  const { data: coupons, loading: counponLoading } = useRequest(
    async () => {
      if (details && details.length) {
        const matchResult = await getMatchedCoupons(
          details,
          getPayPrice(details)
        );
        if (matchResult.length) {
          setSelectedCoupons([matchResult[0]]);
        } else {
          setSelectedCoupons([]);
        }
        return matchResult;
      }
    },
    {
      refreshDeps: [details],
    }
  );

  const payParams = useMemo(() => {
    if (details && details.length && !counponLoading && coupons) {
      const skus = details
        .filter((d) => d.skuId && d.type !== "plusSku")
        .map((item) => {
          return {
            sku_id: item.skuId,
            quantity: item.number,
            is_upgrade: scene === "upgrade" && item.isUpgrade,
            ...(item.is_vip_addition ? { is_vip_addition: true } : null),
          };
        });

      const plusSkus = details.filter((d) => d.type === "plusSku");
      skus.forEach((sku) => {
        sku.promo_addition_skus = plusSkus
          .filter((item) => item.mainSkuId === sku.sku_id)
          .map((item) => ({
            sku_id: item.skuId,
            quantity: item.number,
          }));
      });

      const is_auto_renew = !!details
        .filter((d) => d.good)
        .find((d) => d.good.is_auto_renew);

      const activities = details
        .filter((d) => d.activity)
        .map((d) => ({ id: d.activity.activityId }));

      return {
        skus,
        scene,
        promotion: {
          activities,
          user_coupon_ids: selectedCoupons
            ? selectedCoupons.map((c) => c.id)
            : [],
        },
        is_auto_renew,
        ...(scene == "normal" ? { isVip } : {}),
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counponLoading, coupons, scene, selectedCoupons, isVip]);

  // 优惠券优惠价格
  const couponsPrice = useMemo(() => {
    if (selectedCoupons) {
      return round(
        selectedCoupons.reduce((prev, s) => s.discount + prev, 0),
        2
      );
    } else {
      return 0;
    }
  }, [selectedCoupons]);

  const [, couponFormatCount] = useCountDown({
    targetDate: selectedCoupons[0]?.valid_end_time,
  });

  // 总共优惠价格：优惠券+折扣+活动
  // todo：活动价格优惠计算
  const totalDiscountPrice = (-discount?.price || 0) + couponsPrice;

  return (
    <>
      <div className={classNames(styles.payBottom, styles[theme])} {...rest}>
        <div className={styles.pay}>
          <div className={styles.payQrcodeWrap}>
            {payParams ? (
              <>
                <Pay
                  className={styles.payQrcode}
                  skuParam={payParams || {}}
                  onDone={onOrderDone}
                  onFaild={onOrderFaild}
                  onOrderChange={onOrderChangeWrap}
                  onPaid={onOrderPaid}
                  size={90}
                  padding={0}
                  // disabledFn={false}
                  ref={payRef}
                  data-gd-expose="pay_expose"
                />
              </>
            ) : (
              <Skeleton.Avatar
                active
                shape="square"
                style={{ width: 90, height: 90, marginBottom: 6 }}
              />
            )}
            <div className={styles.payMethods}>
              <HBIcon
                className={styles.payIcon}
                type="a-icon-wechatpay"
                style={{
                  color: "#59CC0D",
                }}
              />
              <HBIcon
                className={styles.payIcon}
                type="a-icon-alipay"
                style={{
                  color: "#06B4FD",
                }}
              />
              扫码支付
            </div>
          </div>

          <div className={styles.info}>
            <>
              {/* 优惠券 */}
              {coupons && coupons.length > 0 && (
                <HBPopover
                  overlayClassName={styles.couponsPopover}
                  placement="topRight"
                  content={
                    <CouponList
                      selected={selectedCoupons}
                      list={coupons}
                      details={details}
                      onChange={setSelectedCoupons}
                    />
                  }
                  onVisibleChange={setIsCouponArrowUp}
                >
                  <div className={styles.couponsPanel}>
                    <div className={styles.couponTag}>券</div>
                    <div className={styles.label}>
                      {!selectedCoupons?.length
                        ? "不使用优惠券"
                        : `立减￥${couponsPrice} `}
                    </div>
                    <div className={styles.expireTime}>
                      {/* 有选择优惠券，且小于三天有效期显示 */}
                      {!!selectedCoupons?.length &&
                        couponFormatCount.days < 3 &&
                        `（${
                          couponFormatCount.days
                            ? couponFormatCount.days + "天"
                            : ""
                        }${couponFormatCount.hours}:${
                          couponFormatCount.minutes
                        }:${couponFormatCount.seconds} 后失效）`}
                    </div>

                    <HBIcon
                      className={isCouponArrowUp ? styles.reverse : ""}
                      type="ic_arrow_down"
                    />
                  </div>
                </HBPopover>
              )}
              <div className={styles.price}>
                <div>应付金额</div>
                <div className="color-primary">
                  <span className={styles.num}>
                    ¥{details ? getPayPrice(details, couponsPrice) : "--"}
                  </span>
                  <HBPopover
                    placement="top"
                    content={
                      <DetailPopoverContent
                        details={details}
                        coupons={selectedCoupons}
                      />
                    }
                  >
                    <span className={styles.checkDetail}>
                      {totalDiscountPrice > 0
                        ? `共优惠￥${totalDiscountPrice}`
                        : "价格明细"}

                      <HBIcon type="ic_info" />
                    </span>
                  </HBPopover>
                </div>
              </div>
            </>
            {/* {payParams ? (
              <></>
            ) : (
              <>
                <Skeleton
                  active
                  title={{ width: "40%", style: { height: 30, margin: 0 } }}
                  paragraph={false}
                ></Skeleton>
                <Skeleton
                  active
                  title={{ width: 60, style: { height: 14, marginTop: 12 } }}
                  paragraph={{
                    rows: 1,
                    width: "50%",
                    style: { margin: "0 0" },
                  }}
                  className={styles.priceSkeleton}
                ></Skeleton>
              </>
            )} */}

            <div className={styles.protocol}>
              支付即视为您同意
              <a
                href="/about/disclaimer?activeKey=2"
                target="_blank"
                rel="noreferrer"
              >
                《花瓣素材会员服务协议》
              </a>
              和
              {vipType === "company" ? (
                <a
                  href="/about/disclaimer?activeKey=5"
                  target="_blank"
                  rel="noreferrer"
                >
                  《花瓣企业会员授权许可协议》
                </a>
              ) : (
                <a
                  href="/about/disclaimer?activeKey=6"
                  target="_blank"
                  rel="noreferrer"
                >
                  《花瓣个人会员授权许可协议》
                </a>
              )}
              {uniqBy(protocols, "href")?.map((p) => (
                <span key={p.name}>
                  ，
                  <a href={p.href} target="_blank" rel="noreferrer">
                    《{p.name}》
                  </a>
                </span>
              ))}
              <div className={styles.rightTop}>
                <HBPopover
                  title={null}
                  placement="topRight"
                  content={
                    <div className={styles.noticeInfo}>
                      <span>
                        ·
                        企业会员指我们将合法保护您的企业授权身份；不提供多账号协作功能
                      </span>
                    </div>
                  }
                >
                  <a>
                    购买须知
                    <HBIcon type="ic_info" style={{ paddingLeft: 4 }} />
                  </a>
                </HBPopover>
              </div>
            </div>
          </div>

          {disabled && (
            <div className={styles.disabledMask}>
              <div>正在支付...</div>
              <div
                className={classNames(styles.cancel, {
                  [styles.disabled]: countdown > 0,
                })}
                onClick={onCancelOrder}
              >
                取消支付
                {countdown !== 0 && `(${Math.round(countdown / 1000)}s)`}
              </div>
            </div>
          )}

          {hasSubscribeTip && <SubscribeTIpMask />}
        </div>
        {!payParams?.is_auto_renew && (
          <div
            className={styles.transferBtn}
            onClick={() =>
              onTransfer({ details, coupons: selectedCoupons, payParams })
            }
          >
            我要对公转账
            <HBIcon type="ic_chevron_right" />
          </div>
        )}
      </div>
    </>
  );
}

function SubscribeTIpMask() {
  const { rightData } = useContext(VipDialogContext);
  const { subscription } = rightData;

  const [state, setState] = useState(true);

  if (state && subscription && subscription.length) {
    const sub = subscription[0];

    return (
      <div className={classNames(styles.disabledMask)}>
        <div className={styles.subscribeTip}>
          <HBIcon type="ic_info" />
          新订单支付成功后，将会取消当前自动续费
        </div>
        <div className={styles.subscribeInfo}>
          当前自动续费方案：{sub.items[0].content.name}
        </div>
        <div className={styles.cancel} onClick={() => setState(false)}>
          我知道了
        </div>
      </div>
    );
  }

  return null;
}
